/**
 * Чистые правила строки создания на главной (ТЗ главной § 3, § 6, § 9, § 10, § 17): без alias-импортов
 * и без React — их проверяет `tests/home-composer.test.ts`.
 */

/** Кириллица в поле: поиск не идёт, облачко «Нужно английское слово» (ТЗ § 10). `\b` кириллицу не
 *  видит, поэтому — класс символов. */
export const hasCyrillic = (text: string): boolean => /[а-яё]/i.test(text);

/** Enter создаёт не раньше чем через 400 мс после показа ответа: ответ из кэша поднимает лист
 *  мгновенно, и Enter по привычке сразу после слова тратил бы жетон вслепую (ТЗ § 6). */
export const ENTER_DELAY_MS = 400;

export interface EnterGate {
  /** Лист открыт. */
  sheetOpen: boolean;
  /** На экране ответ `found` на текущее слово и модуль, а не прошлый приглушённый. */
  foundCurrent: boolean;
  /** Сколько значений отмечено. */
  selected: number;
  /** Когда показан этот ответ в открытом листе; null — ещё не показан. */
  shownAt: number | null;
  now: number;
  /** Создаём, проверяем исход или перечитываем ответ («Проверяем…»). */
  busy: boolean;
  /** Кнопка неактивна: жетоны закончились при выборе с генерацией. */
  blocked: boolean;
}

/** Создаёт ли Enter в поле — то же, что кнопка «Создать», при всех условиях сразу (ТЗ § 6). */
export function canEnterCreate(gate: EnterGate): boolean {
  return (
    gate.sheetOpen &&
    gate.foundCurrent &&
    gate.selected > 0 &&
    !gate.busy &&
    !gate.blocked &&
    gate.shownAt !== null &&
    gate.now - gate.shownAt >= ENTER_DELAY_MS
  );
}

export interface PressPick {
  pos: string | null;
  translation: string;
}

/** Тело нажатия: слово, модуль и выбор (или перевод ручной карточки). */
export interface PressBody {
  word: string;
  moduleId: string;
  picks?: readonly PressPick[];
  phrases?: readonly { front: string; picks: readonly PressPick[] }[];
  /** Ручная карточка — свой перевод. */
  translation?: string;
}

const norm = (text: string) => text.trim().toLowerCase();
const pickKey = (p: PressPick) => `${norm(p.pos ?? '')}:${norm(p.translation)}`;

/**
 * Подпись тела нажатия (ТЗ § 10): тот же `generation_id` уходит снова, только если после неизвестного
 * исхода нажали ещё раз с тем же телом — слово, модуль и отмеченные значения. Порядок отметок не важен.
 */
export function pressSignature(body: PressBody): string {
  const picks = (body.picks ?? []).map(pickKey).sort();
  const phrases = (body.phrases ?? []).map((p) => `${norm(p.front)}=${p.picks.map(pickKey).sort().join(',')}`).sort();
  return JSON.stringify([norm(body.word), body.moduleId, picks, phrases, body.translation == null ? null : norm(body.translation)]);
}

/** Что показывает кнопка действия строки на компьютере (ТЗ § 6, § 10). */
export type CreateButtonKind = 'checking' | 'manual' | 'exhausted' | 'allAdded' | 'pickSense' | 'create';

export interface CreateButtonState {
  /** Режим ручной карточки. */
  manual: boolean;
  /** Неизвестный исход нажатия: проверяем, создались ли карточки. */
  checkingOutcome: boolean;
  /** Ответ словаря перечитывается: слово или модуль поменяли при открытом листе. */
  rereading: boolean;
  creating: boolean;
  /** Жетоны закончились при выборе с генерацией. */
  blocked: boolean;
  /** Все значения уже учатся или создаются. */
  allLocked: boolean;
  /** Сколько карточек получится из отмеченного. */
  cards: number;
}

/**
 * «Проверяем…» при неизвестном исходе — раньше всех веток, в том числе у ручной карточки (ТЗ § 10:
 * «ручная карточка — так же»). Перечитывание ответа словаря держит только создание по словарю: ручная
 * карточка от него не зависит.
 */
export function createButtonKind(state: CreateButtonState): CreateButtonKind {
  if (state.checkingOutcome) return 'checking';
  if (state.manual) return 'manual';
  if (state.rereading && !state.creating) return 'checking';
  if (state.blocked) return 'exhausted';
  if (state.allLocked) return 'allAdded';
  if (state.cards === 0) return 'pickSense';
  return 'create';
}

/** Проверка исхода после обрыва связи (ТЗ § 10): повтор раз в 1,5 с, не дольше 20 с. */
export const CHECK_RETRY_MS = 1500;
export const CHECK_LIMIT_MS = 20_000;

export type CheckStep = 'created' | 'not_created' | 'wait' | 'timeout';

/**
 * Что ответила проверка `GET /generations/{id}`: успех — только `processing: false` и созданные
 * карточки (пачка в работе ещё может откатиться); 404 — не создалось; иначе ждём, пока не выйдут 20 с.
 */
export function checkStep(result: { processing: boolean; total: number } | 'not_found', elapsedMs: number): CheckStep {
  if (result === 'not_found') return 'not_created';
  if (!result.processing) return result.total > 0 ? 'created' : 'not_created';
  return elapsedMs >= CHECK_LIMIT_MS ? 'timeout' : 'wait';
}

/** Лист не выше низа блока тренировки + 24 px, но не ниже 320 px; не выше верха main + 16 px и 600 px (ТЗ § 6). */
export const SHEET_HERO_GAP = 24;
export const SHEET_MIN = 320;
export const SHEET_MAX = 600;
export const SHEET_TOP_GAP = 16;

/**
 * Предельная высота листа над строкой. `rowTop` — верх строки, `areaTop` — верх видимой области
 * (верх main или видимой части окна при клавиатуре iPad), `heroBottom` — низ блока на месте
 * тренировки, если он виден (иначе null). Если до блока меньше 320 px, лист всё равно 320 px:
 * на низких окнах он заходит на тренировку.
 */
export function sheetMaxHeight({
  rowTop,
  areaTop,
  heroBottom,
}: {
  rowTop: number;
  areaTop: number;
  heroBottom: number | null;
}): number {
  const ceiling = rowTop - (areaTop + SHEET_TOP_GAP);
  const byHero = heroBottom !== null && heroBottom > areaTop ? rowTop - (heroBottom + SHEET_HERO_GAP) : Infinity;
  return Math.max(0, Math.min(Math.max(byHero, SHEET_MIN), ceiling, SHEET_MAX));
}

/** Превью этапа 1 на телефоне (ТЗ § 7): 8 px над полем; не выше видимой области над строкой минус
 *  12 px и не выше 480 px; не ниже 120 px — телефон боком с клавиатурой: видны слово и плашка,
 *  середина прокручивается. */
export const PREVIEW_GAP = 8;
export const PREVIEW_MARGIN = 12;
export const PREVIEW_MIN = 120;
export const PREVIEW_MAX = 480;

/** Предел высоты превью; `spaceAbove` — от верха видимой области до верха строки. */
export function previewMaxHeight(spaceAbove: number): number {
  const room = Math.floor(spaceAbove - PREVIEW_GAP - PREVIEW_MARGIN);
  return Math.min(PREVIEW_MAX, Math.max(PREVIEW_MIN, room));
}

/**
 * Ошибка словарного поиска в листе и в превью (ТЗ § 10): без связи — ждём её без «Повторить»
 * (поиск повторится сам), 429 и 5xx — свои тексты с «Повторить», 403 — почта не подтверждена.
 */
export function lookupErrorView(reason: string): { key: string; retry: boolean } {
  switch (reason) {
    case 'offline':
    case 'network':
      return { key: 'homeComposer.offline', retry: false };
    case 'rate_limit':
      return { key: 'cardsWorkspace.errors.rateLimit', retry: true };
    case 'email':
      return { key: 'cardsWorkspace.errors.emailNotVerified', retry: false };
    default:
      return { key: 'homeComposer.lookupError', retry: true };
  }
}

/** Поиск в меню модулей — если модулей больше 6, заголовки групп — если больше 4 (ТЗ § 9). */
export const MENU_SEARCH_OVER = 6;
export const MENU_HEADERS_OVER = 4;
export const MENU_RECENT = 3;

export interface ModuleMenuModel<M> {
  /** «Недавние» — до трёх из истории, только те, что есть в свежем списке. */
  recent: M[];
  /** «Все модули» — в порядке сервера, без недавних. */
  all: M[];
  headers: boolean;
  search: boolean;
  /** По запросу ничего не нашлось. */
  empty: boolean;
}

export function buildModuleMenu<M extends { id: string; title: string }>(
  modules: readonly M[],
  recentIds: readonly string[],
  query: string,
): ModuleMenuModel<M> {
  const q = norm(query);
  const matches = (m: M) => !q || m.title.toLowerCase().includes(q);
  const recent: M[] = [];
  for (const id of recentIds) {
    const found = modules.find((m) => m.id === id);
    if (found && !recent.includes(found)) recent.push(found);
    if (recent.length >= MENU_RECENT) break;
  }
  const all = modules.filter((m) => !recent.includes(m));
  const shownRecent = recent.filter(matches);
  const shownAll = all.filter(matches);
  return {
    recent: shownRecent,
    all: shownAll,
    headers: modules.length > MENU_HEADERS_OVER,
    search: modules.length > MENU_SEARCH_OVER,
    empty: shownRecent.length + shownAll.length === 0,
  };
}

/** Буква модуля для чипа 44×44 и значка строки меню. */
export function moduleLetter(title: string): string {
  const first = Array.from(title.trim())[0];
  return first ? first.toLocaleUpperCase('ru-RU') : '·';
}

/** До стольких знаков имя модуля в облачке не рвётся: такое имя встаёт в строку и на 320 px. */
export const KEEP_MODULE_NAME_CHARS = 24;

/**
 * Имя модуля для фразы облачка («Ручная карточка в «Мои слова» — …»): короткое — с неразрывными пробелами.
 * `text-wrap: balance` ради ровных строк рвал его внутри кавычек: «в «Мои» / «слова» — …» (R2-10). Длинное
 * переносится как есть — одной строкой на узком экране оно бы не встало.
 */
export function unbreakableModuleName(title: string): string {
  return Array.from(title).length <= KEEP_MODULE_NAME_CHARS ? title.replace(/ /g, '\u00a0') : title;
}

export type ComposerErrorEvent =
  | 'ALREADY_ADDED'
  | 'GENERATION_IN_PROGRESS'
  | 'MODULE_NOT_FOUND'
  | 'QUOTA'
  | 'RATE_LIMIT'
  | 'EMAIL'
  | 'NETWORK'
  | 'SERVER'
  | 'UNKNOWN';

/** Код ошибки создания → значение `code` события `home_composer_error` (ТЗ § 17). */
export function composerErrorEvent(code: string): ComposerErrorEvent {
  switch (code) {
    case 'ALREADY_ADDED':
    case 'GENERATION_IN_PROGRESS':
    case 'MODULE_NOT_FOUND':
    case 'RATE_LIMIT':
    case 'NETWORK':
    case 'SERVER':
      return code;
    case 'GENERATION_PART_IN_PROGRESS':
      return 'NETWORK';
    case 'DAILY_LIMIT_REACHED':
      return 'QUOTA';
    case 'EMAIL_NOT_VERIFIED':
      return 'EMAIL';
    default:
      return 'UNKNOWN';
  }
}
